import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'

import type { PublicBranchSummary } from '@/bindings/PublicBranchSummary'
import type { PublicRestaurant } from '@/bindings/PublicRestaurant'
import { Badge } from '@/components/badge'
import { ChevronRightIcon } from '@/components/icons'
import { Card, Page } from '@/components/layout'
import { LandingFeedbackButtons } from '@/features/feedback/landing-buttons'
import { branchPath, restaurantPageQuery } from '@/features/landing/api'
import { useCustomerAuth } from '@/features/ordering/customer-auth'
import { CustomerHeader, CustomerLoadError, CustomerLoading, CustomerShell } from '@/features/ordering/customer-chrome'
import { useLocalizedText } from '@/lib/i18n/localized-text'
import { useT } from '@/lib/i18n/translate'
import { canManageRestaurant } from '@/lib/roles'

const ROUTE = '/$restaurantSlug'

/**
 * `/<restaurant-slug>` (PRD "Restaurant landing page"): the intro, the
 * branch list, and for a manager/superuser the way into the back office.
 * Staff and admins find theirs on the branch page.
 */
export function RestaurantLandingPage() {
  const { restaurantSlug } = useParams({ from: ROUTE })
  const t = useT()
  const auth = useCustomerAuth()
  const page = useQuery(restaurantPageQuery(restaurantSlug))

  if (page.isPending) return <CustomerLoading />
  if (page.error) return <CustomerLoadError error={page.error} notFound={t('landing.notFound')} retry={() => void page.refetch()} />

  const { restaurant, branches, my_role } = page.data
  // 2026-09-12 lock: the name and a neutral notice — never why, and no way in.
  if (restaurant.deactivated) {
    return (
      <CustomerShell color={restaurant.color}>
        <CustomerHeader restaurant={restaurant.name} auth={auth} />
        <Page width="md" className="flex flex-1 flex-col gap-4">
          <Card className="p-5">
            <p className="text-muted">{t('landing.unavailable')}</p>
          </Card>
        </Page>
      </CustomerShell>
    )
  }
  return (
    <CustomerShell color={restaurant.color}>
      <CustomerHeader restaurant={restaurant.name} auth={auth} />
      <Page width="md" className="flex flex-1 flex-col gap-4">
        {my_role && canManageRestaurant(my_role) && (
          <Link
            to="/$restaurantSlug/manage/branches"
            params={{ restaurantSlug }}
            className="bg-accent text-accent-fg hover:bg-accent-hover min-h-tap flex items-center justify-center rounded-lg px-4 font-medium sm:self-start"
          >
            {t('landing.manageRestaurant')}
          </Link>
        )}
        <Intro restaurant={restaurant} />
        <section aria-labelledby="branches-heading" className="flex flex-col gap-2">
          <h2 id="branches-heading" className="text-lg font-semibold">
            {t('landing.branchesTitle')}
          </h2>
          {branches.length === 0 ? (
            <p className="text-muted text-sm">{t('landing.noBranches')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {branches.map((branch) => (
                <li key={branch.id}>
                  <BranchLink restaurantSlug={restaurantSlug} branch={branch} />
                </li>
              ))}
            </ul>
          )}
        </section>
        <LandingFeedbackButtons auth={auth} restaurant={restaurant} />
      </Page>
    </CustomerShell>
  )
}

/** The restaurant's intro through the T4.3 chain (E6: the main language may be missing). */
export function Intro({ restaurant }: { restaurant: PublicRestaurant }) {
  const text = useLocalizedText(restaurant.main_language)(restaurant.intro)
  if (!text) return null
  return (
    <Card className="p-5">
      <p className="whitespace-pre-line">{text}</p>
    </Card>
  )
}

function BranchLink({ restaurantSlug, branch }: { restaurantSlug: string; branch: PublicBranchSummary }) {
  const t = useT()
  return (
    <Link
      to={branchPath(restaurantSlug, branch.slug)}
      className="bg-surface border-border hover:border-accent hover:bg-accent-soft active:bg-accent-soft min-h-tap flex items-center justify-between gap-3 rounded-xl border px-4 py-3 shadow-sm transition-colors"
    >
      <span className="flex min-w-0 flex-col">
        <span className="text-accent-ink font-medium">{branch.name}</span>
        {branch.address && <span className="text-muted truncate text-sm">{branch.address}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <Badge tone={branch.is_open ? 'success' : 'muted'}>{branch.is_open ? t('landing.open') : t('landing.closed')}</Badge>
        <ChevronRightIcon className="text-muted" />
      </span>
    </Link>
  )
}
